import { Badge, Button, Text, Tooltip } from '@mantine/core';
import { IconFileImport } from '@tabler/icons-react';

import {
  INSERTION_LABELS,
  RegistryDigestAddToReportModal,
  type RegistryDigestInsertionMode,
} from '@/components/Modals/RegistryDigestAddToReportModal';
import {
  isReportStageTakingContent,
  registryDigestInsertionState,
  type RegistryDigestAddedToReport,
  type RegistryDigestAddToReportPreview,
  type RegistryDigestStatus,
  type RegistryReportContent,
} from '@/types/registry-documents';
import { reportStageWords } from './digestErrors';

/** What the "Not in the latest version" pill says. */
export const NOT_PRESENT_WORDS =
  'A later version of the report no longer carries this block; add it again.';

/** What the "Out of date" pill says. */
export const OUT_OF_DATE_WORDS = 'The report holds an earlier extraction; update to replace it.';

/** Which insertion the control offers: the first add, an update of the
 * block the report still carries (up to date or from an earlier run), or
 * adding again once a later version of the report dropped it (the same
 * call as the first add). */
export function insertionModeOf(status: RegistryDigestStatus): RegistryDigestInsertionMode {
  const mark = status.addedToReport;
  if (!mark) {
    return 'add';
  }
  return mark.present ? 'update' : 'again';
}

export interface AddToReportControlProps {
  companyName: string;
  status: RegistryDigestStatus;
  reportContent: RegistryReportContent | null;
  /** The confirmation dialog is open. Kept by the block, which hides its
   * own error line while the dialog shows the same words. */
  opened: boolean;
  onOpen: () => void;
  onClose: () => void;
  isAddingToReport: boolean;
  /** Why the last preview or write was refused, from the hook. */
  error: Error | null;
  onPreview: () => Promise<RegistryDigestAddToReportPreview | null>;
  onAdd: (expectedContentVersion: number) => Promise<RegistryDigestAddedToReport | null>;
  /** The write succeeded: the surfaces re-read the order and the report. */
  onAdded: () => void;
}

/**
 * "Add to report" / "Update in report" / "Add again" with its dialog,
 * beside the other ways to take the digest into Word, for a Word report
 * and a Hub-written one alike. Nothing where the surface knows nothing of
 * the report's document (the API predates the signal). Disabled, with
 * the reason, at a stage the API would refuse anyway (`report_stage`):
 * the tooltip saves the click. "Out of date" marks a block in the report
 * from an earlier run than the one shown; "Not in the latest version"
 * marks a block a later version of the report dropped, when the button
 * reads "Add again".
 */
export function AddToReportControl({
  companyName,
  status,
  reportContent,
  opened,
  onOpen,
  onClose,
  isAddingToReport,
  error,
  onPreview,
  onAdd,
  onAdded,
}: Readonly<AddToReportControlProps>) {
  if (registryDigestInsertionState(status, reportContent) === 'hidden' || !reportContent) {
    return null;
  }
  const mode = insertionModeOf(status);
  const blocked = isReportStageTakingContent(reportContent.lifecycleStage)
    ? null
    : reportStageWords(reportContent.lifecycleStage);

  // `data-disabled` rather than `disabled`: a disabled button takes no
  // pointer events, so its tooltip could never show the reason.
  const button = (
    <Button
      size="compact-xs"
      variant="light"
      leftSection={<IconFileImport size={14} />}
      loading={isAddingToReport}
      data-disabled={blocked !== null || undefined}
      aria-disabled={blocked !== null || undefined}
      onClick={(event) => {
        if (blocked !== null) {
          event.preventDefault();
          return;
        }
        onOpen();
      }}
      data-testid="registry-digest-add-to-report"
    >
      {INSERTION_LABELS[mode]}
    </Button>
  );

  return (
    <>
      {blocked === null ? button : <Tooltip label={blocked}>{button}</Tooltip>}
      {mode === 'again' && (
        <Tooltip label={NOT_PRESENT_WORDS}>
          <Badge
            size="xs"
            variant="light"
            color="orange"
            tabIndex={0}
            data-testid="registry-digest-not-present"
          >
            Not in the latest version
          </Badge>
        </Tooltip>
      )}
      {mode === 'update' && status.addedToReport?.current === false && (
        <Tooltip label={OUT_OF_DATE_WORDS}>
          <Badge
            size="xs"
            variant="light"
            color="orange"
            tabIndex={0}
            data-testid="registry-digest-out-of-date"
          >
            Out of date
          </Badge>
        </Tooltip>
      )}
      <RegistryDigestAddToReportModal
        opened={opened}
        onClose={onClose}
        companyName={companyName}
        mode={mode}
        reportContent={reportContent}
        onPreview={onPreview}
        onConfirm={onAdd}
        error={error}
        onAdded={onAdded}
      />
    </>
  );
}

/** "Added to report · version N" under the actions, and where the report
 * has moved on since, which version it is at now. Nothing until the first
 * add, or where the control itself is hidden. */
export function AddedToReportLine({
  status,
  reportContent,
}: Readonly<{ status: RegistryDigestStatus; reportContent: RegistryReportContent | null }>) {
  const mark = status.addedToReport;
  if (!mark || registryDigestInsertionState(status, reportContent) === 'hidden') {
    return null;
  }
  const now = reportContent?.version ?? null;
  const movedOn =
    now !== null && now > mark.version ? ` · the report is now at version ${now}` : '';
  return (
    <Text size="xs" c="dimmed" data-testid="registry-digest-added-to-report">
      Added to report · version {mark.version}
      {movedOn}
    </Text>
  );
}
